import { Type } from '@angular/core';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { DirtyFormGuard } from '../guards/dirty-form.guard';

export const ROOT_PATH = 'navigation';
export const CUSTOM_PAGE_PATH = 'custom';
export const LOGIN_PATH = 'login';

export enum RootPath {
  CONFIGURATION = 'configuration',
  MAPS_EDIT = 'maps-edit',
  MONITORING = 'monitoring',
  REPORTS = 'reports',
  SYSCONFIG = 'sysconfig',
}

export enum ConfigurationPath {
  ANCHORS_MAP_SELECT = 'anchors-map-select',
  ANCHORS_MAP_2D = 'anchors-map-2d',
  LE_CONFIG = 'le-config',
  ANCHORS = 'anchors-configuration',
  TAGS = 'tags-configuration',
  MULTITAGS = 'multitags-configuration',
}

export enum MapsEditPath {
  MAPS_LIST = 'maps-list',
  PLANS_LIST = 'plans-list',
}

export enum MonitoringPath {
  RANGING_MAP_SELECT = 'ranging-map-select',
  RANGING_MAP_2D = 'ranging-map-2d',
  RANGING_MAP_3D = 'ranging-map-3d',
  TAGS = 'tags-monitoring',
  ANCHORS = 'anchors-monitoring',
  ANCHOR_PAIRS = 'anchors-pairs',
}

export enum ReportsPath {
  EVENTS = 'events',
  ANCHOR_STATUS = 'anchor-status',
}

export enum SysconfigPath {
  SYSTEM_MODULES = 'system-modules',
  API_KEYS = 'api-keys',
  USERS = 'users',
  ACCOUNT = 'account',
  UI_CONFIG = 'ui-config',
}

export type PathMap = {
  [key: string]: unknown;
  [RootPath.CONFIGURATION]: ConfigurationPath;
  [RootPath.MAPS_EDIT]: MapsEditPath;
  [RootPath.MONITORING]: MonitoringPath;
  [RootPath.REPORTS]: ReportsPath;
  [RootPath.SYSCONFIG]: SysconfigPath;
};

// Used to construct routes in angular routing modules
export interface ContainsRoute<T> {
  path: T;
}

export interface MainRouteDescription<T> extends ContainsRoute<T> {
  // canLoad: [typeof RouteAllowedGuard],
  // canActivate: [typeof RouteAllowedGuard],
  // canActivateChild: [typeof RouteAllowedGuard],
}

export interface ContextRouteDescription<T> extends ContainsRoute<T> {
  component: Type<any>;
  canDeactivate: [typeof DirtyFormGuard];
}

// Main menu section
export type SubmenuItem = {
  // For navigation menu
  title: string;
  hidden?: boolean;
};

export type MainMenuItem<T extends RootPath> = {
  // For navigation menu
  title: string;
  icon: string;
  iconTheme: string;
  iconClass?: string;
  pages: { [P in PathMap[T]]: SubmenuItem };
  divider?: boolean;
};

export type MainMenu = {
  [P in RootPath]: MainMenuItem<P>;
};

export const mainMenu: MainMenu = {
  [RootPath.CONFIGURATION]: {
    title: marker('navigation.menu.configuration.title'),
    icon: 'settings',
    iconTheme: 'configuration-theme',
    pages: {
      [ConfigurationPath.ANCHORS_MAP_SELECT]: {
        title: marker('navigation.menu.configuration.anchorsMapSelect'),
      },
      [ConfigurationPath.ANCHORS_MAP_2D]: {
        title: marker('navigation.menu.configuration.anchorsMap2d'),
        hidden: true,
      },
      [ConfigurationPath.LE_CONFIG]: {
        title: marker('navigation.menu.configuration.leConfig'),
      },
      [ConfigurationPath.ANCHORS]: {
        title: marker('navigation.menu.configuration.anchors'),
      },
      [ConfigurationPath.TAGS]: {
        title: marker('navigation.menu.configuration.tags'),
      },
      [ConfigurationPath.MULTITAGS]: {
        title: marker('navigation.menu.configuration.multitags'),
      },
    },
  },
  [RootPath.MAPS_EDIT]: {
    title: marker('navigation.menu.mapsEdit.title'),
    icon: 'map',
    iconTheme: 'configuration-theme',
    pages: {
      [MapsEditPath.MAPS_LIST]: {
        title: marker('navigation.menu.mapsEdit.mapsList'),
      },
      [MapsEditPath.PLANS_LIST]: {
        title: marker('navigation.menu.mapsEdit.plansList'),
      },
    },
  },
  [RootPath.MONITORING]: {
    title: marker('navigation.menu.monitoring.title'),
    icon: 'pie_chart',
    iconTheme: 'runtime-theme',
    pages: {
      [MonitoringPath.RANGING_MAP_SELECT]: {
        title: marker('navigation.menu.monitoring.rangingMapSelect'),
      },
      [MonitoringPath.RANGING_MAP_2D]: {
        title: marker('navigation.menu.monitoring.rangingMap2d'),
        hidden: true,
      },
      [MonitoringPath.RANGING_MAP_3D]: {
        title: marker('navigation.menu.monitoring.ranging3d'),
      },
      [MonitoringPath.TAGS]: {
        title: marker('navigation.menu.monitoring.tags'),
      },
      [MonitoringPath.ANCHORS]: {
        title: marker('navigation.menu.monitoring.anchors'),
      },
      [MonitoringPath.ANCHOR_PAIRS]: {
        title: marker('navigation.menu.monitoring.anchorPairs'),
      },
    },
  },
  [RootPath.REPORTS]: {
    title: marker('navigation.menu.reports.title'),
    icon: 'receipt',
    iconTheme: 'history-theme',
    pages: {
      [ReportsPath.EVENTS]: {
        title: marker('navigation.menu.reports.events'),
      },
      [ReportsPath.ANCHOR_STATUS]: {
        title: marker('navigation.menu.reports.anchorStatus'),
      },
    },
  },
  [RootPath.SYSCONFIG]: {
    title: marker('navigation.menu.sysconfig.title'),
    icon: 'build',
    iconTheme: 'configuration-theme',
    pages: {
      [SysconfigPath.SYSTEM_MODULES]: {
        title: marker('navigation.menu.sysconfig.systemModules'),
      },
      [SysconfigPath.API_KEYS]: {
        title: marker('navigation.menu.sysconfig.apiKeys'),
      },
      [SysconfigPath.USERS]: {
        title: marker('navigation.menu.sysconfig.users'),
      },
      [SysconfigPath.ACCOUNT]: {
        title: marker('navigation.menu.sysconfig.account'),
      },
      [SysconfigPath.UI_CONFIG]: {
        title: marker('navigation.menu.sysconfig.uiConfig'),
      },
    },
  },
};
